import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { io } from 'socket.io-client';
import { endLiveSession } from '../Redux/Dashboard/DashboardSlice';
import { Notify } from '../CommonPopup/DashboardPopUp';
import { IoCloseCircleOutline } from 'react-icons/io5';
import '../Style/LiveSession.css';

// Initialize socket instance
const socket = io('http://localhost:8080', {
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
});

const getYouTubeEmbedUrl = (url) => {
  try {
    const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
    const match = url.match(regex);
    return match && match[1] ? `https://www.youtube.com/embed/${match[1]}` : null;
  } catch (error) {
    console.error('Error parsing YouTube URL:', error);
    return null;
  }
};

const SessionView = ({ session, onBack, isAdmin }) => {
  const dispatch = useDispatch();
  const [allComments, setAllComments] = useState(session.comments || []);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const commentsEndRef = useRef(null);

  useEffect(() => {
    debugger;
    if (!session.is_active) {
      return;
    }
    // Ensure socket is connected
    if (!socket.connected) {
      socket.connect();
      console.log('Attempting to reconnect socket');
    }

    // Emit join_session only once initially
    socket.emit('join_session', session._id);
    console.log(`Emitted join_session for session: ${session._id}`);

    // Handle new comments
    const handleNewComment = (data) => {
      console.log('Received new comment:', data);
      setAllComments((prev) => {
        const commentExists = prev.some(
          (c) => c.text === data.comment.text && c.user._id === data.comment.user._id
        );
        if (!commentExists) {
          return [...prev, data.comment];
        }
        return prev;
      });
    };

    socket.on('new_comment', handleNewComment);

    // Handle reconnect: Rejoin room only on reconnect
    const handleConnect = () => {
      console.log('Connected to Socket.IO server');
      socket.emit('join_session', session._id); // Rejoin room on reconnect
    };

    socket.on('connect', handleConnect);

    // Handle connection errors
    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    // Handle tab/window close
    const handleBeforeUnload = () => {
      socket.disconnect();
      console.log('Socket disconnected on tab close');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      socket.off('new_comment', handleNewComment);
      socket.off('connect', handleConnect);
      socket.off('connect_error');
      socket.disconnect();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      console.log('Socket disconnected on page leave');
    };
  }, [session._id]);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allComments]);

  const handleEndSession = async () => {
    try {
      await dispatch(endLiveSession({ sessionId: session._id })).unwrap();
      Notify({ data: 'success', msg: 'Session ended successfully', type: 'success' });
      setShowEndSessionModal(false);
      onBack();
    } catch (err) {
      Notify({ data: 'error', msg: err.message || 'Failed to end session', type: 'error' });
    }
  };

  return (
    <div className="LiveSessionView">
      <button className="LiveSessionBtn LiveSessionBtnSecondary LiveSessionBackBtn" onClick={onBack}>
        Back to Sessions
      </button>
      <h3 className="LiveSessionViewTitle">{session.title}</h3>
      <p className="LiveSessionDescription">{session.description}</p>
      <div className="LiveSessionVideoContainer">
        <iframe
          src={getYouTubeEmbedUrl(session.youtube_link)}
          title={session.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="LiveSessionVideo"
        />
      </div>
      <div className="LiveSessionControls">
        {session.is_active  && (
          <button
            className="LiveSessionBtn LiveSessionBtnDanger"
            onClick={() => setShowEndSessionModal(true)}
          >
            End Session
          </button>
        )}
      </div>
        <h4 className="LiveSessionCommentsTitle">Comments</h4>
      <div className="LiveSessionTableContainer">
        <table className="LiveSessionCommentsTable">
          <thead>
            <tr>
              <th>User</th>
              <th>Comment</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {allComments.length > 0 ? (
              [...allComments]
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((comment, index) => (
                  <tr key={index}>
                    <td>{comment.name}</td>
                    <td>{comment.text}</td>
                    <td>{new Date(comment.timestamp).toLocaleTimeString()}</td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan="3" className="LiveSessionNoData">
                  No comments yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div ref={commentsEndRef} />
      </div>

      {/* End Session Modal */}
      {showEndSessionModal && (
        <div className="LiveSessionModalOverlay">
          <div className="LiveSessionModalContent">
            <div className="LiveSessionModalHeader">
              <h5>Confirm End Session</h5>
              <IoCloseCircleOutline
                size={28}
                onClick={() => setShowEndSessionModal(false)}
                style={{ cursor: 'pointer', color: '#FF4D4F' }}
              />
            </div>
            <div className="LiveSessionModalBody">
              <p>
                Are you sure you want to end{' '}
                <strong className="LiveSessionTextDanger LiveSessionBold">{session.title}</strong>?
              </p>
            </div>
            <div className="LiveSessionModalFooter">
              <button
                className="LiveSessionBtn LiveSessionBtnSecondary"
                onClick={() => setShowEndSessionModal(false)}
              >
                Cancel
              </button>
              <button
                className="LiveSessionBtn LiveSessionBtnDanger"
                onClick={handleEndSession}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionView;